import { readEntitlementFailure } from '@/lib/entitlement-failure';
import type { SoulSaveDependencies, SoulSaveJob } from './soul-save-job';
import type { ApiResponse } from './api/types';
import type { SoulSaveSession } from '@/types/subject-model';

function requireSession(response: ApiResponse<SoulSaveSession | null>): SoulSaveSession {
  if (response.success === false || response.error || !response.data) throw new Error('soulSaveConfirming');
  return response.data;
}

/** Browser transmits bytes. The server session owns confirmation and admission. */
export async function runServerSoulSaveJob(job: SoulSaveJob, deps: SoulSaveDependencies, resumeUpload: boolean, acceptedCredits?: number): Promise<void> {
  if (!deps.server) throw new Error('soulSaveUnavailable');
  const server = deps.server;
  const save = async () => { deps.assertOwner(); await deps.persist(job); deps.assertOwner(); };
  const reconcile = async (session: SoulSaveSession): Promise<boolean> => {
    deps.assertOwner();
    if (session.request_id !== job.id) throw new Error('soulSaveUnavailable');
    job.sessionRegistered = true;
    job.entitlementFailure = readEntitlementFailure(session) ?? undefined;
    job.recoverable = session.recoverable;
    if (session.stage === 'cancelled' && !session.model) {
      job.stage = 'rejected'; job.errorKey = 'soulSaveCancelled'; await save(); return true;
    }
    if (!session.model && (session.photos.length !== job.photos.length
      || session.photos.some((photo, index) => photo.photo_id !== job.photos[index].id))) throw new Error('soulSaveUnavailable');
    for (let index = 0; index < session.photos.length; index++) {
      const receipt = session.photos[index].receipt;
      if (receipt?.asset_id && receipt.url) {
        job.photos[index].receipt = receipt;
        delete job.photos[index].blob;
        delete job.photos[index].checkpoint;
      }
    }
    if (session.model?.soul_identity?.task_id) {
      for (const photo of job.photos) { delete photo.blob; delete photo.checkpoint; }
      job.stage = 'completed';
      job.errorKey = undefined;
      job.modelId = session.model.id;
      await save();
      deps.onModel(session.model);
      return true;
    }
    if (session.stage === 'failed' || session.stage === 'cancelled') {
      job.stage = 'rejected';
      job.errorKey = session.error_code === 'INSUFFICIENT_CREDITS' ? 'soulInsufficientCredits'
        : session.error_code === 'SOUL_ID_SUBSCRIPTION_REQUIRED' ? 'soulSubscriptionExpired'
        : session.error_code === 'SOUL_ID_PRICE_CHANGED' ? 'soulPriceChanged'
        : session.error_code === 'SOUL_ID_SAVE_EXPIRED' ? 'soulSaveExpired' : 'soulCreateFailed';
      await save();
      return true;
    }
    if (session.stage === 'admitting' || session.stage === 'accepted') {
      job.stage = 'submitting';
      job.errorKey = undefined;
      await save();
      return true;
    }
    if (session.stage === 'uploading' && job.stage === 'rejected') {
      job.stage = 'uploading'; job.errorKey = undefined; await save(); return true;
    }
    return false;
  };
  deps.assertOwner();
  if (job.stage === 'completed') return;
  if (job.stage === 'rejected') {
    if (acceptedCredits === undefined) {
      const observed = await server.get(job.id);
      deps.assertOwner();
      if (observed.success !== false && !observed.error && observed.data) await reconcile(observed.data);
      return;
    }
    if (!server.resume || !Number.isFinite(acceptedCredits) || acceptedCredits < 0 || !job.recoverable) throw new Error('soulSaveUnavailable');
    // A start-time refusal has no server session yet. Only authoritative null,
    // observed during this explicit confirmation, permits recreating the free intake.
    const observed = await server.get(job.id);
    deps.assertOwner();
    if (observed.success === false || observed.error) throw observed.error ?? new Error('soulSaveUnavailable');
    // Persist the accepted intent before POST so a lost response can be observed
    // without replaying an admission or reverting to the previous local quote.
    job.credits = acceptedCredits;
    await save();
    const resumed = observed.data === null
      ? await server.ensure({ ...job, credits: acceptedCredits })
      : await server.resume(job.id, acceptedCredits);
    deps.assertOwner();
    if (resumed.success === false || resumed.error || !resumed.data) {
      job.entitlementFailure = readEntitlementFailure(resumed) ?? job.entitlementFailure;
      await save();
      throw resumed.error ?? new Error('soulSaveUnavailable');
    }
    job.credits = acceptedCredits;
    // Persist the server state even if this response was recovered after a network loss.
    if (!await reconcile(resumed.data)) {
      job.stage = 'uploading'; job.errorKey = undefined; await save();
    }
    return;
  }
  let transmitting = false;
  try {
    // Replaying this free, owner-scoped session registration preserves exactly
    // one intake task. There is no browser training-admission POST to lose.
    const admission = job.sessionRegistered ? await server.get(job.id) : await server.ensure(job);
    deps.assertOwner();
    if (admission.error && ['SOUL_ID_SUBSCRIPTION_REQUIRED', 'SOUL_ID_PRICE_CHANGED', 'SOUL_ID_UNAVAILABLE', 'SOUL_ID_SAVE_LIMIT', 'SOUL_ID_SAVE_REQUEST_CHANGED', 'SOUL_ID_DUPLICATE_PHOTOS', 'SOUL_ID_INVALID_NAME', 'VALIDATION_ERROR', 'HTTP_422'].includes(admission.error.code)) {
      job.stage = 'rejected';
      job.entitlementFailure = readEntitlementFailure(admission) ?? undefined;
      job.recoverable = ['SOUL_ID_SUBSCRIPTION_REQUIRED', 'SOUL_ID_PRICE_CHANGED', 'INSUFFICIENT_CREDITS'].includes(admission.error.code);
      job.errorKey = admission.error.code === 'SOUL_ID_SUBSCRIPTION_REQUIRED' ? 'soulSubscriptionExpired'
        : admission.error.code === 'SOUL_ID_PRICE_CHANGED' ? 'soulPriceChanged'
        : admission.error.code === 'SOUL_ID_UNAVAILABLE' ? 'soulUnavailable'
        : admission.error.code === 'SOUL_ID_SAVE_LIMIT' ? 'soulSaveLimit' : 'soulCreateFailed';
      await save(); return;
    }
    if (await reconcile(requireSession(admission))) return;
    if (job.stage === 'upload_failed' && !resumeUpload) { await save(); return; }
    transmitting = true;
    job.stage = 'uploading';
    job.errorKey = undefined;
    await save();
    for (let index = 0; index < job.photos.length; index++) {
      deps.assertOwner();
      const photo = job.photos[index];
      if (!photo.receipt && photo.checkpoint) {
        const response = await deps.confirm(photo.checkpoint);
        deps.assertOwner();
        if (response.success !== false && !response.error && response.data?.asset_id && response.data.url) photo.receipt = response.data;
        else if (resumeUpload && response.error?.code === 'material_upload_object_missing') { delete photo.checkpoint; await save(); }
        else throw new Error('subjectModelUploadFailed');
      }
      if (!photo.receipt) {
        photo.receipt = await deps.upload(photo, async (checkpoint) => {
          photo.checkpoint = checkpoint;
          await save();
          // Server must own this exact object intent before PUT. Even if the
          // browser never observes PUT/confirm acknowledgement, intake continues.
          const session = requireSession(await server.register(job.id, index, checkpoint.asset_id, checkpoint));
          deps.assertOwner();
          if (session.stage !== 'uploading') throw new Error('soulSaveConfirming');
        });
      }
      if (!photo.receipt?.asset_id || !photo.receipt.url) throw new Error('subjectModelUploadFailed');
      delete photo.blob;
      delete photo.checkpoint;
      await save();
      if (await reconcile(requireSession(await server.register(job.id, index, photo.receipt.asset_id)))) return;
    }
    transmitting = false;
    job.stage = 'submitting';
    job.errorKey = undefined;
    await save();
    await reconcile(requireSession(await server.get(job.id)));
  } catch (error) {
    deps.assertOwner();
    // Server state is recovered on the next observation, including local upload
    // failures whose GCS objects were successfully confirmed by the intake worker.
    job.stage = transmitting ? 'upload_failed' : job.stage;
    job.errorKey = transmitting ? 'subjectModelUploadFailed' : error instanceof Error && error.message === 'soulSaveUnavailable'
      ? 'soulSaveUnavailable' : 'soulSaveConfirming';
    await save();
  }
}
